/******/ (() => { // webpackBootstrap
class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    // DOM elements
    this.input = this.querySelector('[data-predictive-search-input]');
    this.submitButton = this.querySelector('[data-predictive-search-submit]');
    this.clearButton = this.querySelector('[data-predictive-search-clear]');
    this.resultsContainer = this.querySelector('[data-predictive-search-results]');
    this.emptyStateContainer = this.querySelector('[data-predictive-search-empty-state]');
    this.boxShadowElement = this.closest('.popup-shadow');

    // Initial state
    this.isPredictive = this.getAttribute('is-predictive') === 'true';
    this.searchQuery = this.input.value;
    this.searchChangeTimer = null;
  
    // Bind event handlers
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleInputKeyDown = this.handleInputKeyDown.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleClear = this.handleClear.bind(this);
    this.handleClearButtonVisibility = this.handleClearButtonVisibility.bind(this);
    this.handleNavButtonClick = this.handleNavButtonClick.bind(this);
    this.resetState = this.resetState.bind(this);
  }

  connectedCallback() {
    window.wetheme.webcomponentRegistry.register({key: 'component-predictive-search'});

    // Add event listeners
    this.input.addEventListener('input', this.handleInputChange);
    this.input.addEventListener('keydown', this.handleInputKeyDown);
    this.submitButton.addEventListener('click', this.handleSubmit);
    this.clearButton.addEventListener('click', this.handleClear);
  }

  handleInputChange() {
    this.handleClearButtonVisibility();

    if (!this.isPredictive) return;

    if (this.input.value !== '') {
      this.searchQuery = this.input.value;

      if (this.searchChangeTimer) {
        window.clearTimeout(this.searchChangeTimer);
      }

      this.searchChangeTimer = window.setTimeout(() => {
        this.performSearch(this.searchQuery);
        this.searchChangeTimer = null;
      }, 250);
    }
    else {
      this.resetState();
    }
  }

  handleClearButtonVisibility() {
    if (this.input.value === '') {
      this.clearButton.classList.add('fade-out');
      setTimeout(() => {
        this.clearButton.classList.add('hide');
        this.clearButton.classList.remove('fade-out');
      }, 250);
    }
    else {
      this.clearButton.classList.remove('hide');
    }
  }

  handleInputKeyDown(event) {
    if (event.key === 'Enter') {
      this.handleSubmit();
    }
  }

  handleSubmit() {
    // HRS: if predictive results already show a vehicle-match card, go to that collection.
    // Otherwise try a small synonym map so Enter still works before results finish loading.
    // Product/keyword searches are unchanged (same trailing-* search URL as Flow).
    const vehicleLink =
      this.resultsContainer &&
      this.resultsContainer.querySelector('a[data-hrs-vehicle-redirect], a.hrs-predictive-vehicle-match__link');
    if (vehicleLink && vehicleLink.getAttribute('href')) {
      window.location.href = vehicleLink.getAttribute('href');
      return;
    }

    const mapped = this.resolveVehicleCollectionUrl(this.input.value);
    if (mapped) {
      window.location.href = mapped;
      return;
    }

    window.location.href = `${window.Shopify.routes.root}search?q=${encodeURIComponent(this.input.value)}*`;
  }

  /**
   * Normalize a query the same way Liquid does (lowercase, strip spaces/hyphens/punctuation)
   * and map common vehicle codes to collection URLs. Keep in sync with
   * sections/predictive-search-results.liquid.
   */
  resolveVehicleCollectionUrl(rawQuery) {
    if (!rawQuery) return null;
    const key = String(rawQuery)
      .toLowerCase()
      .trim()
      .replace(/[\s\-–—‑/.]/g, '');
    if (!key) return null;

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const to = (path) => (root.endsWith('/') ? root.slice(0, -1) : root) + path;

    if (key.includes('gtr') || key.includes('r35')) return to('/collections/gt-r');
    // Exact "rc" only (normalized). Avoid substring false-positives like random "rc" in longer words.
    if (key === 'rc') return to('/collections/lexus-rc');
    if (
      key.includes('rcf') ||
      key.includes('rc350f') ||
      key.includes('lexusrc') ||
      key.includes('rc350') ||
      key.includes('rc300')
    ) {
      return to('/collections/lexus-rc');
    }
    if (key.includes('lc500') || key.includes('lexuslc')) return to('/collections/lexus-lc');
    if (key.includes('is350') || key.includes('is500') || key.includes('lexusis')) return to('/collections/lexus-is');
    if (key.includes('es350') || key.includes('lexuses')) return to('/collections/lexus-es');
    if (key.includes('gx460') || key.includes('gx550') || key.includes('lexusgx')) return to('/collections/lexus-gx');
    if (key.includes('nx300') || key.includes('nx350') || key.includes('lexusnx')) return to('/collections/lexus-nx');
    if (key.includes('rx350') || key.includes('lexusrx')) return to('/collections/lexus-rx');
    if (key.includes('q50') || key.includes('infinitiq50')) return to('/collections/infiniti');
    if (key.includes('gr86') || key.includes('brz') || key.includes('frs') || key.includes('ft86')) {
      return to('/collections/toyota-subaru');
    }
    if (key.includes('camry')) {
      if (key.includes('2025') || key.includes('2026')) return to('/collections/2025-toyota-camry');
      return to('/collections/2018-toyota-camry');
    }
    if (key.includes('accord')) {
      if (key.includes('2023') || key.includes('2024') || key.includes('2025') || key.includes('2026') || key.includes('11thgen')) {
        return to('/collections/11th-gen-honda-accord');
      }
      if (key.includes('2021') || key.includes('2022') || key.includes('105gen')) return to('/collections/accord-10-5-gen');
      if (key.includes('2018') || key.includes('2019') || key.includes('2020') || key.includes('10thgen')) {
        return to('/collections/accord-10th-gen');
      }
      return to('/collections/accord');
    }
    if (key.includes('civic')) {
      if (key.includes('2022') || key.includes('2023') || key.includes('2024') || key.includes('2025') || key.includes('2026') || key.includes('11thgen')) {
        return to('/collections/honda-civic-11th-gen');
      }
      if (key.includes('hatch') || key.includes('fk7') || key.includes('fk8') || key.includes('typer')) {
        return to('/collections/civic-hb');
      }
      if (
        key.includes('2016') ||
        key.includes('2017') ||
        key.includes('2018') ||
        key.includes('2019') ||
        key.includes('2020') ||
        key.includes('2021') ||
        key.includes('10thgen')
      ) {
        return to('/collections/civic-sedan');
      }
      return to('/collections/honda-civic-11th-gen');
    }
    if (key.includes('challenger') || key.includes('dodgechallenger') || (key.includes('dodge') && !key.includes('ram'))) {
      return to('/collections/dodge');
    }
    if (key.includes('tundra')) return to('/collections/tundra');
    if (key.includes('landcruiser')) return to('/collections/land-cruiser');
    if (key.includes('4runner')) return to('/collections/toyota-4runner');
    if (key.includes('tacoma')) return to('/collections/toyota-tacoma');
    if (key.includes('fjcruiser')) return to('/collections/fj-cruiser');
    if (key.includes('corolla')) return to('/collections/corolla');
    if (key.includes('altima')) return to('/collections/altima');
    if (key.includes('armada')) return to('/collections/armada');
    if (key.includes('mustang')) return to('/collections/mustang');
    if (key.includes('tesla') || key.includes('model3') || key.includes('modely')) return to('/collections/tesla');
    if (key.includes('bmw')) return to('/collections/bmw');
    return null;
  }

  handleClear() {
    this.input.value = '';
    this.searchQuery = '';
    this.handleClearButtonVisibility();
    this.input.focus();
    this.resetState();
  }

  async performSearch(query) {
    const url = `${window.Shopify.routes.root}search/suggest?q=${encodeURIComponent(query)}&section_id=predictive-search-results&resources[limit]=10&resources[limit_scope]=each&resources[type]=query,product,collection,page,article`;

    try {
      const response = await fetch(url);
      const responseText = await response.text();

      if (response.status === 417) {
        return;
      }

      if (this.input.value === '') return;

      if (this.emptyStateContainer) {
        this.emptyStateContainer.setAttribute('hidden', '');
      }

      // Render the results
      const doc = new DOMParser().parseFromString(responseText, 'text/html');
      const content = doc.querySelector('.shopify-section');
      if (content) {
        this.renderSearchResults(content.innerHTML);
      }
    } catch (error) {
      console.error(`Failed to fetch search results:`, error);
    }
  }

  renderSearchResults(html) {
    this.resultsContainer.innerHTML = html;

    // Remove any previously tracked event listeners
    if (this.boundNavButtons) {
      this.boundNavButtons.forEach(el => {
        el.removeEventListener('click', this.handleNavButtonClick);
      });
    }

    // Track elements with event listeners for later cleanup
    this.boundNavButtons = [];

    // Add nav button event listeners
    const navButtons = this.resultsContainer.querySelector('[data-predictive-search-nav-buttons]');
    if (navButtons) {
      navButtons.addEventListener('click', this.handleNavButtonClick);
      this.boundNavButtons.push(navButtons);
    }
  }

  handleNavButtonClick(e) {
    e.stopPropagation();
    const navButton = e.target;
    const resultId = navButton.getAttribute('data-nav-id');
    if (!resultId) return;

    const results = this.resultsContainer.querySelector(`[data-grid-id="${resultId}"]`);
    if (!results) return;

    const previousActiveButton = this.resultsContainer.querySelector(`[data-nav-id].active`);
    const previousActiveResult = this.resultsContainer.querySelector(`[data-grid-id]:not([hidden])`);

    // Set active nav button
    if (previousActiveButton) {
      previousActiveButton.setAttribute('aria-selected', 'false');
      previousActiveButton.classList.remove('active');
    }
    navButton.setAttribute('aria-selected', 'true');
    navButton.classList.add('active');

    // Set active results
    const heightContainer = this.resultsContainer.querySelector('[data-predictive-search-main-results]');
    const beforeHeight = heightContainer.clientHeight;

    if (previousActiveResult) {
      previousActiveResult.setAttribute('hidden', '');
    }
  
    results.removeAttribute('hidden');

    // Transition the height change
    heightContainer.animate([
      { height: `${beforeHeight}px` },
      { height: `${heightContainer.clientHeight}px` }
    ], { duration: 500, easing: 'cubic-bezier(0.85, 0, 0.15, 1)' });
  }

  resetState() {
    if (this.searchChangeTimer) {
      window.clearTimeout(this.searchChangeTimer);
    }

    if (this.emptyStateContainer) {
      this.emptyStateContainer.removeAttribute('hidden');
      this.renderSearchResults('');
      return;
    }

    this.resultsContainer.classList.add('fade-out');
    if (this.boxShadowElement) {
      this.boxShadowElement.classList.remove('popup-shadow');
    }

    setTimeout(() => {
      this.renderSearchResults('');
      this.resultsContainer.classList.remove('fade-out');
      if (this.boxShadowElement) {
        this.boxShadowElement.classList.add('popup-shadow');
      }
    }, 300);
  }

  disconnectedCallback() {
    // Remove event listeners
    this.input.removeEventListener('input', this.handleInputChange);
    this.input.removeEventListener('keydown', this.handleInputKeyDown);
    this.submitButton.removeEventListener('click', this.handleSubmit);
    this.clearButton.removeEventListener('click', this.handleClear);

    // Clean up dynamic listeners
    if (this.boundNavButtons) {
      this.boundNavButtons.forEach(el => {
        el.removeEventListener('click', this.handleNavButtonClick);
      });
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);

/******/ })()
;